import { useCallback, useRef, useState } from 'react';
import { sendStreamingChatMessage, ChatStreamError } from '../../../../services/chatService';
import { Message, ImageGenerationData, GeneratedPostsData, MessageActions, UIAction, SocialDescriptionItem, BlogPost, OutreachEmailDraft, CallsAttachment, CardContactsAttachment, GhlContactResultsAttachment, GhlConversationData, GhlInboxData } from '../types';
import type { AvailabilityAttachment, BookingAttachment, SendDraftAttachment } from '../types/ghlBookingChat';
import { parseSendDraftToolResult } from '../utils/parseSendDraftToolResult';
import { parseAvailabilityToolResult } from '../utils/parseAvailabilityToolResult';
import { parseBookingToolResult } from '../utils/parseBookingToolResult';
import {
  parseContactSearchResult,
  parseGetMessagesResult,
  parseListConversationsResult,
} from '../utils/parseGhlToolResults';
import { MediaItem } from '../../../../types/MediaItem';
import { handleStreamEvent } from '../utils/streamHandler';
import { assistantStandInText } from '../utils/assistantStandInText';
import { isGbpReviewToolCall } from '../utils/gbpReviewRequest';
import { parseAskUserResult, type AskUserQuestion } from '../askUser';
import { UGCVideoStreamData } from '../../../../features/ugc-video/types';
import { debug } from "../../../../utils/debugLogger";
import { isCommunicationAgentType } from "../../../../constants/agentTypes";
import { COMMUNICATION_AGENT_STARTER_ACTIONS } from "../config/actionConfig";
import { blogToChatMarkdown } from "../../../../features/wordpress/platforms/wordpress/utils/blogContent";

/** Messages still being answered, by agent and text, shared by every chat mounted
 *  in this tab (module scope, so a remounted chat sees them too). The same
 *  message never goes out twice while its first copy is being answered: on
 *  2026-10-11 one "fix my gbp" reached chat-service twice, 35 s apart, each time
 *  right after the chat window mounted, and became two conversations and two
 *  Business Profile proposals. Released when the reply ends, or after MAX_MS. */
const IN_FLIGHT_SENDS = new Map<string, number>();
const IN_FLIGHT_MAX_MS = 5 * 60_000;
export const inFlightSendKey = (agentId: string, content: string) => `${agentId}|${content.trim()}`;
/** True (and recorded) when this send may go; false when the same one is still being answered. */
export function claimSend(key: string, now = Date.now()): boolean {
  const started = IN_FLIGHT_SENDS.get(key);
  if (started !== undefined && now - started < IN_FLIGHT_MAX_MS) return false;
  IN_FLIGHT_SENDS.set(key, now);
  return true;
}
export const releaseSend = (key: string) => void IN_FLIGHT_SENDS.delete(key);

/** opensCard: the step's result opens an inline card when the turn ends (the GBP fix card).
 *  stage: what a running Business Profile proposal is doing now (gbp_plan_progress, SGL-421). */
export type ToolStep = { tool: string; state: 'running' | 'done'; opensCard?: boolean; stage?: string };

// Helper function to generate conversation title from first user message
const generateConversationTitle = (firstMessage: string): string => {
  let title = firstMessage.trim();

  // Remove common prefixes
  const prefixes = ['Hello', 'Hi', 'Hey', 'Can you help', 'I need'];
  for (const prefix of prefixes) {
    if (title.toLowerCase().startsWith(prefix.toLowerCase())) {
      title = title.substring(prefix.length).trim();
      break;
    }
  }

  // Capitalize and truncate
  title = title.charAt(0).toUpperCase() + title.slice(1);
  return title.length > 50
    ? title.substring(0, 47) + '...'
    : title || 'New Conversation';
};

interface UseChatStreamingOptions {
  agentId: string | undefined;
  /** Outreach campaign started in this thread, sent as request context. */
  activeCampaignId?: string | null;
  /** Used to ignore content-creator `starter_actions` from the stream for SMS/email agents. */
  agentType?: string;
  customerId: string | undefined;
  idToken: string;
  model?: string;
  /** Provider selected in the chat UI (e.g. 'openai', 'perplexity'). Defaults to 'openai'. */
  selectedProvider?: string;
  /** Model selected in the chat UI. Overrides the agent-level model when set. */
  selectedChatModel?: string;
  vectorStoreId?: string;
  threadId: string | null;
  conversationId: string | null;
  messages: Message[];
  conversationTitle: string | null;
  
  // State setters
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setIsLoading: (loading: boolean) => void;
  setError: (error: ChatStreamError | string | null) => void;
  setStreamingContent: React.Dispatch<React.SetStateAction<string>>;
  setStreamingThinkingContent: React.Dispatch<React.SetStateAction<string>>;
  setStreamingActions: (actions: MessageActions | null) => void;
  setCurrentStatus: (status: 'thinking' | 'tool_selected' | 'tool_executing' | 'generating' | null) => void;
  setCurrentToolName: (toolName: string | null) => void;
  setStreamingGeneratedImages: (images: ImageGenerationData | null) => void;
  /** True once the CURRENT generate_image_batch call's images have arrived. Separate
   *  from streamingGeneratedImages, which accumulates across every image call in the
   *  turn (including an earlier generate_image before the batch one) and so can't
   *  tell "this batch is done" from "some earlier image already streamed in". */
  setBulkImagesReady: (ready: boolean) => void;
  setStreamingGeneratedPosts: (posts: GeneratedPostsData | null) => void;
  setStreamingUiActions: (actions: UIAction[] | null) => void;
  /** Perplexity citation URLs, updated progressively during streaming (SGL-101). */
  setStreamingCitations: (citations: string[] | null) => void;
  setStarterActions?: (actions: UIAction[] | null) => void;
  
  // Thread/conversation management
  setThreadId: (threadId: string | null) => void;
  setConversationId: (conversationId: string | null) => void;
  setConversationTitle: (title: string | null) => void;
  onThreadChange?: (threadId: string | null) => void;
  
  // UGC Video callback
  onUGCVideoEvent?: (data: UGCVideoStreamData) => void;
  /** UGC/reels: script chunk or full text from stream; opens studio when provided. */
  onScript?: (delta?: string, fullText?: string) => void;
  /** Called when stream ends with accumulated script (for delta-only script streaming). */
  onScriptEnd?: (accumulatedScript: string) => void;
  /** UGC/reels: platform captions/tags from social_description event; opens studio. */
  onSocialDescription?: (items: SocialDescriptionItem[]) => void;
  /** Called when social_posts event is received; parent can open right panel and show this data. */
  onSocialPostsPanelRequest?: (data: GeneratedPostsData) => void;
  /** Called when a blog event is received (output_blog); parent opens the blog editor with this data. */
  onBlogPanelRequest?: (blog: BlogPost) => void;
  /**
   * Called the moment the model selects the output_blog tool (before the blog content
   * arrives). Lets the parent open the blog panel in its "Writing…" loading state — but
   * ONLY for a genuine blog, since a general prompt in blog mode never selects output_blog.
   */
  onBlogGenerationStart?: () => void;
  /**
   * Called when the SSE stream for a sonar-deep-research request ends (cleanly or via
   * error) without delivering a research_document event. The parent should start polling
   * the /chat/research-status/{thread_id} endpoint to pick up the document once the
   * backend finishes (the document is always persisted regardless of SSE connectivity).
   * `startedAt` is epoch ms the run began (from the SSE start event) so the parent can
   * keep the loading card's elapsed timer accurate while polling.
   */
  onDeepResearchDisconnected?: (threadId: string, startedAt: number) => void;
  /**
   * Called when the SSE stream for a generate_image_batch request ends (cleanly or via
   * error/abort) without delivering that batch's terminal images event. Mirrors
   * onDeepResearchDisconnected — the parent should start polling
   * /chat/image-batch-status/{thread_id} to pick up the result once the backend finishes
   * (the batch keeps running and persisting server-side regardless of SSE connectivity).
   * `startedAt` is epoch ms the batch began (from the SSE tool_selected event).
   */
  onBulkImageDisconnected?: (threadId: string, startedAt: number) => void;
  /**
   * Called once, after the reply is committed, when the model called
   * review_google_business_profile (SGL-403): the parent opens the Business Profile card.
   * `threadId` is the thread this stream reported, which the parent's own state is still
   * a render behind on. Not called when the stream fails or is cancelled.
   */
  /** `plan` is the SGL-421 proposal the tool prepared (its `gbp_plan` event), unchecked. */
  onGbpReviewRequested?: (info: { threadId: string | null; plan?: unknown }) => void;
  /** SGL-421: the agent changed a row of the open Business Profile card (`gbp_row_update`),
   *  unchecked. Applied as it arrives: the card is already on screen. */
  onGbpRowUpdate?: (update: unknown) => void;
  /** SGL-428: the agent called ask_user; the parent shows the question as a dialog. */
  onAskUser?: (question: AskUserQuestion) => void;

  // Additional callbacks
  onClearUGCVideoSelections?: () => void;
  setInputMessage?: (message: string) => void;
  setSelectedImages?: (images: MediaItem[]) => void;
}

export const useChatStreaming = (options: UseChatStreamingOptions) => {
  const {
    agentId,
    activeCampaignId,
    agentType,
    customerId,
    idToken,
    model,
    selectedProvider,
    selectedChatModel,
    vectorStoreId,
    threadId,
    conversationId,
    messages,
    conversationTitle,
    setMessages,
    setIsLoading,
    setError,
    setStreamingContent,
    setStreamingThinkingContent,
    setStreamingActions,
    setCurrentStatus,
    setCurrentToolName,
    setStreamingGeneratedImages,
    setBulkImagesReady,
    setStreamingGeneratedPosts,
    setStreamingUiActions,
    setStreamingCitations,
    setStarterActions,
    setThreadId,
    setConversationId,
    setConversationTitle,
    onThreadChange,
    onUGCVideoEvent,
    onScript,
    onScriptEnd,
    onSocialDescription,
    onSocialPostsPanelRequest,
    onBlogPanelRequest,
    onBlogGenerationStart,
    onClearUGCVideoSelections,
    setInputMessage,
    setSelectedImages,
    onDeepResearchDisconnected,
    onBulkImageDisconnected,
    onGbpReviewRequested,
    onGbpRowUpdate,
    onAskUser,
  } = options;

  const [streamingAvailability, setStreamingAvailability] = useState<AvailabilityAttachment | null>(null);
  const [streamingBooking, setStreamingBooking] = useState<BookingAttachment | null>(null);
  const [streamingGhlContacts, setStreamingGhlContacts] = useState<GhlContactResultsAttachment | null>(null);
  const [streamingGhlConversation, setStreamingGhlConversation] = useState<GhlConversationData | null>(null);
  const [streamingGhlInbox, setStreamingGhlInbox] = useState<GhlInboxData | null>(null);
  const [toolSteps, setToolSteps] = useState<ToolStep[]>([]);

  // --- Streaming-content coalescer ---------------------------------------
  // The model streams many text deltas per second. Calling setStreamingContent
  // on every delta re-renders the whole chat window (+ reparses the full
  // markdown) per token, which makes the reply visibly lag. Coalesce token
  // updates to ~one React state update per animation frame (~60fps). Clears
  // ('') apply immediately and drop any queued frame, so a stale token flush
  // can never re-show the preview after the final message has been committed.
  const pendingStreamRef = useRef<string | null>(null);
  const streamRafRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const setStreamingContentThrottled = useCallback(
    (text: string) => {
      if (text === '') {
        pendingStreamRef.current = null;
        if (streamRafRef.current !== null) {
          cancelAnimationFrame(streamRafRef.current);
          streamRafRef.current = null;
        }
        setStreamingContent('');
        return;
      }
      pendingStreamRef.current = text;
      if (streamRafRef.current === null) {
        streamRafRef.current = requestAnimationFrame(() => {
          streamRafRef.current = null;
          if (pendingStreamRef.current !== null) {
            setStreamingContent(pendingStreamRef.current);
            pendingStreamRef.current = null;
          }
        });
      }
    },
    [setStreamingContent],
  );

  // --- Reveal-pacing buffer -----------------------------------------------
  // The rAF coalescer above caps *paint frequency* but always paints whatever
  // text has accumulated by that point — if the network delivers tokens in an
  // uneven burst, the UI jumps several words in one frame instead of
  // revealing them steadily. Decouple *visual reveal rate* from *network
  // arrival rate*: buffer newly-arrived text and drain it onto the throttled
  // setter one word at a time on a fixed cadence.
  const revealedTextRef = useRef('');
  const queuedTextRef = useRef('');
  const revealRafRef = useRef<number | null>(null);
  const revealLastTsRef = useRef<number>(0);

  // Baseline reveal speed when the buffer is nearly drained, in characters per
  // second. ~240 cps at 60fps is ~4 chars/frame — small, steady, every-frame
  // updates that read as smooth typing instead of word-at-a-time jumps, while
  // keeping the trailing type-out after the model finishes short (~0.7s).
  const REVEAL_MIN_CPS = 240;
  // Ceiling on the drain rate. Even when a big network burst leaves a large
  // backlog, never reveal faster than this (~10 chars/frame at 60fps) so a
  // burst still reads as fast smooth typing rather than a chunky 30-char jump.
  const REVEAL_MAX_CPS = 600;
  // When a burst leaves a backlog, aim to clear it within this window (ms). The
  // drain rate scales with the backlog between the min/max bounds above so we
  // catch up quickly without ever dumping a whole burst in one frame.
  const REVEAL_CATCHUP_MS = 350;

  const clearRevealTimer = useCallback(() => {
    if (revealRafRef.current !== null) {
      cancelAnimationFrame(revealRafRef.current);
      revealRafRef.current = null;
    }
    revealLastTsRef.current = 0;
  }, []);

  // Single rAF-driven drain loop. Each frame reveals a *time-normalized* number
  // of characters (not words), so the visual reveal rate is decoupled from the
  // uneven network arrival rate and stays low-variance frame to frame — the
  // property that makes Claude/ChatGPT streaming feel smooth.
  const drainReveal = useCallback(
    (ts: number) => {
      const dt = revealLastTsRef.current ? ts - revealLastTsRef.current : 16;
      revealLastTsRef.current = ts;

      const target = queuedTextRef.current;
      const shown = revealedTextRef.current.length;
      const remaining = target.length - shown;
      if (remaining <= 0) {
        revealRafRef.current = null;
        revealLastTsRef.current = 0;
        return;
      }

      // Adaptive rate: at least the baseline, faster when backlogged so a big
      // burst clears within REVEAL_CATCHUP_MS instead of lagging behind — but
      // capped at REVEAL_MAX_CPS so no single frame reveals a chunky jump.
      const cps = Math.min(
        REVEAL_MAX_CPS,
        Math.max(REVEAL_MIN_CPS, (remaining / REVEAL_CATCHUP_MS) * 1000),
      );
      let chars = Math.max(1, Math.round((cps * dt) / 1000));
      if (chars > remaining) chars = remaining;

      revealedTextRef.current = target.slice(0, shown + chars);
      setStreamingContent(revealedTextRef.current);
      revealRafRef.current = requestAnimationFrame(drainReveal);
    },
    [setStreamingContent],
  );

  const startRevealTimer = useCallback(() => {
    if (revealRafRef.current !== null) return;
    revealLastTsRef.current = 0;
    revealRafRef.current = requestAnimationFrame(drainReveal);
  }, [drainReveal]);

  // Queue newly-arrived streaming text (delta or resent full text) to be
  // revealed character-by-character instead of painted immediately.
  const revealContent = useCallback(
    (fullText: string) => {
      if (fullText.length <= queuedTextRef.current.length) {
        // Not a simple append (reset/replace/shrink) — sync instantly rather
        // than trying to diff a shrinking string into "new" text.
        clearRevealTimer();
        queuedTextRef.current = fullText;
        revealedTextRef.current = fullText;
        setStreamingContentThrottled(fullText);
        return;
      }
      // Just extend the target; the drain loop paces how fast it turns visible.
      queuedTextRef.current = fullText;
      startRevealTimer();
    },
    [clearRevealTimer, startRevealTimer, setStreamingContentThrottled],
  );

  // Bypass pacing for non-typing content updates (resets, one-shot full-text
  // events like content_with_actions/blog mirror) — keep refs in sync so a
  // later revealContent() call computes the right suffix.
  const setContentImmediate = useCallback(
    (text: string) => {
      clearRevealTimer();
      queuedTextRef.current = text;
      revealedTextRef.current = text;
      setStreamingContentThrottled(text);
    },
    [clearRevealTimer, setStreamingContentThrottled],
  );

  // Wait until the reveal buffer has typed out everything queued (or a safety
  // cap elapses). Called just before committing the final assistant message so
  // the last few characters type out smoothly instead of snapping in when the
  // streamed preview is swapped for the finished message.
  const waitForRevealDrain = useCallback((maxWaitMs = 1200) => {
    return new Promise<void>((resolve) => {
      const start = performance.now();
      const check = () => {
        const caughtUp =
          revealedTextRef.current.length >= queuedTextRef.current.length;
        if (caughtUp || performance.now() - start > maxWaitMs) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }, []);

  const sendMessage = useCallback(
    async (
      content: string,
      selectedImages: MediaItem[] = [],
      options?: { toolPreference?: string; providerOverride?: string; modelOverride?: string }
    ) => {
      if ((!content.trim() && selectedImages.length === 0) || !agentId || !customerId) {
        return;
      }
      // Text-only sends are guarded; a send with images is never a duplicate of one.
      const sendKey = content.trim() && selectedImages.length === 0 ? inFlightSendKey(agentId, content) : null;
      if (sendKey && !claimSend(sendKey)) {
        debug.warn('useChatStreaming: the same message is still being answered; not sending it again');
        return;
      }
      const release = () => {
        if (sendKey) releaseSend(sendKey);
      };

      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      const userMessage: Message = {
        role: 'user',
        content: content.trim(),
        images: selectedImages.length > 0 ? [...selectedImages] : undefined,
      };
      
      setMessages((prev) => [...prev, userMessage]);
      setInputMessage?.('');
      setSelectedImages?.([]);

      if (content.trim() === '/mock-perplexity') {
        setIsLoading(true);
        const id = `mock-perplexity-${Date.now()}`;
        setMessages((prev) => [...prev, {
          id,
          role: 'assistant',
          type: 'perplexity_progress',
          query: 'Mocking perplexity search for design...',
          isComplete: false,
          content: ''
        }]);
        
        setTimeout(() => {
          setMessages((prev) => 
            prev.map(msg => 
              msg.id === id 
                ? { ...msg, isComplete: true } 
                : msg
            )
          );
          
          setTimeout(() => {
            setMessages((prev) => prev.filter(msg => msg.id !== id));
          }, 800);
          
          setIsLoading(false);
        }, 12000);
        release();
        return;
      }

      setIsLoading(true);
      setError(null);
      setContentImmediate('');
      setStreamingThinkingContent('');
      setStreamingActions(null);
      setStreamingUiActions(null);
      setStreamingCitations(null);
      setStreamingAvailability(null);
      setStreamingBooking(null);
      setStreamingGhlContacts(null);
      setStreamingGhlConversation(null);
      setStreamingGhlInbox(null);
      setCurrentStatus('thinking'); // Start with default "Working on it..." status
      setCurrentToolName(null);
      setStreamingGeneratedImages(null);
      setBulkImagesReady(false);
      setStreamingGeneratedPosts(null);
      setToolSteps([]);

      try {
        const outgoing = [...messages, userMessage];
        // Every card turn describes itself in the present tense ("these are the
        // details on the owner's screen"), which is right for one scan and misleading
        // for two — the model was handed two equally-current cards and anchored on the
        // older one. The same marking chat.py applies when it rebuilds from the stored
        // thread; this is the path taken on the first turn and for OpenAI agents.
        const cardTurns = outgoing
          .map((m, i) => (m.cardDetails || m.cardContacts ? i : -1))
          .filter((i) => i >= 0);
        const latestCardTurn = cardTurns.length > 1 ? cardTurns[cardTurns.length - 1] : -1;

        const requestBody: any = {
          agent_id: agentId,
          provider: options?.providerOverride || selectedProvider || 'openai',
          messages: outgoing.map((msg, msgIndex) => {
            const messageObj: any = {
              role: msg.role,
            };

            if (msg.role === 'user') {
              const contentArray: any[] = [];

              if (msg.content && msg.content.trim()) {
                contentArray.push({
                  type: 'input_text',
                  text: msg.content.trim(),
                });
              }

              if (msg.images && msg.images.length > 0) {
                msg.images.forEach((img) => {
                  const isImageAttachment =
                    img.media_type === "IMAGE" ||
                    (img.media_type || "").startsWith("image/") ||
                    (img.cloudfront_url || "").startsWith("data:image");
                  if (isImageAttachment) {
                    contentArray.push({
                      type: 'input_image',
                      image_url: img.cloudfront_url,
                      media_id: img.media_id,
                      media_type: img.media_type,
                    });
                  } else {
                    contentArray.push({
                      type: 'input_file',
                      filename: img.s3_key,
                      file_data: img.cloudfront_url,
                    });
                  }
                });
              }

              messageObj.content = contentArray;
            } else {
              // Assistant messages also need content as array format. A card-only
              // turn has no prose, and an empty content array is a hard Bedrock
              // ValidationException on the NEXT turn ("text content blocks must be
              // non-empty") — so describe the card instead, and drop the message
              // entirely if there is nothing to describe (below).
              const contentArray: any[] = [];
              const text = assistantStandInText(
                msg,
                latestCardTurn < 0
                  ? ""
                  : msgIndex === latestCardTurn
                    ? "latest"
                    : "superseded",
              );
              if (text) {
                contentArray.push({
                  type: 'input_text',
                  text,
                });
              }
              messageObj.content = contentArray;
            }

            return messageObj;
          }).filter((m: any) => m.role === 'user' || (m.content?.length ?? 0) > 0),
          customer_id: customerId,
        };

        if (options?.modelOverride || selectedChatModel || model) {
          requestBody.model = options?.modelOverride || selectedChatModel || model;
        }

        if (vectorStoreId) {
          requestBody.vector_store_id = vectorStoreId;
        }

        // Include thread context if available
        if (threadId) {
          requestBody.thread_id = threadId;
        }
        if (conversationId) {
          requestBody.conversation_id = conversationId;
        }

        if (options?.toolPreference) {
          requestBody.tool_preference = options.toolPreference;
        }

        // The campaign the user just started, if any. Carried on the request
        // rather than written into the thread so the agent can act on it while
        // the person never has to see a UUID.
        if (activeCampaignId) {
          requestBody.active_campaign_id = activeCampaignId;
        }

        // Bulk-image (generate_image_batch) completion email is always on, no user
        // toggle — the backend's notify_email defaults True, so nothing to send here.
        // docs/bulk-image-email-notification-plan.md

        const response = await sendStreamingChatMessage(requestBody, idToken, abortController.signal);

        // Handle SSE streaming response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('No response body');
        }

        let buffer = '';
        let accumulatedText = '';
        let localAccumulatedThinking = '';
        let currentEvent = '';
        // Track streaming data in closure to avoid stale state issues
        let localStreamingGeneratedPosts: GeneratedPostsData | null = null;
        let localStreamingBlog: BlogPost | null = null;
        let localStreamingOutreachEmail: OutreachEmailDraft | null = null;
        let localStreamingCalls: CallsAttachment | null = null;
        let localStreamingCardContacts: CardContactsAttachment | null = null;
        let localStreamingAvailability: AvailabilityAttachment | null = null;
        let localStreamingSendDraft: SendDraftAttachment | null = null;
        let localStreamingBooking: BookingAttachment | null = null;
        let localStreamingGhlContacts: GhlContactResultsAttachment | null = null;
        let localStreamingGhlConversation: GhlConversationData | null = null;
        let localStreamingGhlInbox: GhlInboxData | null = null;
        let localStreamingGeneratedImages: ImageGenerationData | null = null;
        let localStreamingActions: MessageActions | null = null;
        let localStreamingUiActions: UIAction[] | null = null;
        // The model that actually answered this turn (SSE start.model), incl. Auto's per-turn
        // resolution (SGL-368) — captured here so the finished message can show it.
        let localAnsweringModel: string | undefined;
        let localAccumulatedScript = '';
        let localCitations: string[] = [];
        // Deep-research disconnect detection: track whether this was a sonar-deep-research
        // request and whether the SSE stream delivered the research_document event.
        // If the stream ends without the doc, the parent starts polling.
        let isDeepResearchRequest = false;
        let receivedResearchDoc = false;
        let deepResearchStartedAt = 0;
        // Bulk-image batch disconnect detection: mirrors the deep-research flags above.
        let isBulkImageBatchRequest = false;
        let receivedBulkImages = false;
        let bulkImageBatchStartedAt = 0;
        // review_google_business_profile (SGL-403): note the call, act once the reply is
        // committed. The thread is tracked here because the parent's state lags a render.
        let gbpReviewRequested = false;
        // SGL-421: the proposal the tool built, sent just before its tool_call.
        let gbpPlan: unknown = undefined;
        let localThreadId: string | null = threadId;

        const readStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                onScriptEnd?.(localAccumulatedScript);
                // Deep research dropped before the document arrived: the only thing in
                // accumulatedText is the "Generating your research report…" holding
                // message. Don't commit it as a stray bubble — onDeepResearchDisconnected
                // (below) adds the research_pending card, and polling delivers the report.
                if (isDeepResearchRequest && !receivedResearchDoc) {
                  accumulatedText = '';
                  setContentImmediate('');
                }
                // Create final message if we have content, generated images, or generated posts
                // Use local variables to avoid stale closure values
                if (
                  accumulatedText ||
                  localStreamingGeneratedImages ||
                  localStreamingGeneratedPosts ||
                  localStreamingBlog ||
                  localStreamingOutreachEmail ||
                  localStreamingCalls ||
                  localStreamingCardContacts ||
                  localStreamingAvailability ||
                  localStreamingSendDraft ||
                  localStreamingBooking ||
                  localStreamingGhlContacts ||
                  localStreamingGhlConversation ||
                  localStreamingGhlInbox
                ) {
                  const cleanedContent = accumulatedText || '';

                  const finalMessage: Message = {
                    role: 'assistant',
                    content: cleanedContent,
                    answeringModel: localAnsweringModel,
                    calls: localStreamingCalls || undefined,
                    cardContacts: localStreamingCardContacts || undefined,
                    availability: localStreamingAvailability || undefined,
                    sendDraft: localStreamingSendDraft || undefined,
                    booking: localStreamingBooking || undefined,
                    ghlContacts: localStreamingGhlContacts || undefined,
                    ghlConversation: localStreamingGhlConversation || undefined,
                    ghlInbox: localStreamingGhlInbox || undefined,
                  };

                  // Attach actions if they were detected during streaming (legacy + new)
                  if (localStreamingActions) {
                    finalMessage.actions = localStreamingActions;
                  }
                  if (localStreamingUiActions && localStreamingUiActions.length > 0) {
                    finalMessage.uiActions = localStreamingUiActions;
                  }
                  if (localCitations.length > 0) {
                    finalMessage.citations = localCitations;
                  }

                  // Attach generated images if set by tool_call event
                  if (localStreamingGeneratedImages) {
                    finalMessage.generatedImages = localStreamingGeneratedImages;
                    finalMessage.content = 'Here are your generated images! 🎨';
                  }

                  // Attach generated posts if set by social_posts event
                  if (localStreamingGeneratedPosts) {
                    finalMessage.generatedPosts = localStreamingGeneratedPosts;
                    finalMessage.content = 'Here are your social posts! 📝';
                  }

                  // Attach blog if set by the blog event (output_blog). The full blog
                  // (title + Markdown body) is rendered in the chat thread, and the
                  // "Open Canvas" card below it reopens the blog editor panel.
                  if (localStreamingBlog) {
                    finalMessage.blogPost = localStreamingBlog;
                    finalMessage.content = blogToChatMarkdown(localStreamingBlog);
                  }

                  // Attach the outreach email draft if set by the outreach_email event
                  // (output_outreach_email). Rendered as an inline editable card — no
                  // right-panel canvas (unlike blog/social posts, nothing to send yet).
                  if (localStreamingOutreachEmail) {
                    finalMessage.outreachEmail = localStreamingOutreachEmail;
                    finalMessage.content = finalMessage.content || 'Here’s your outreach email draft ✉️';
                  }

                  // Attach the calls list if set by the calls event (output_calls).
                  // Rendered as an inline read-only card, same as the contacts list card.
                  if (localStreamingCalls) {
                    finalMessage.calls = localStreamingCalls;
                    finalMessage.content = finalMessage.content || '';
                  }

                  // Scanned cards + drafts (output_card_contacts, SGL-346). Same
                  // treatment as calls: the agent's own reply still carries the
                  // narrative, the card carries the thing to act on.
                  if (localStreamingCardContacts) {
                    finalMessage.cardContacts = localStreamingCardContacts;
                    finalMessage.content = finalMessage.content || '';
                  }

                  // Attach the availability/booking card if set by the generic
                  // tool_call event (ghl_get_availability / ghl_book_appointment,
                  // SGL-123). Same treatment as calls: the model's own narrative
                  // text still carries the reply, the card supplements it.
                  if (localStreamingAvailability) {
                    finalMessage.availability = localStreamingAvailability;
                    finalMessage.content = finalMessage.content || '';
                  }

                  // SGL-244: ghl_send_message drafts rather than sends, so this
                  // card carries the only control that can actually send.
                  if (localStreamingSendDraft) {
                    finalMessage.sendDraft = localStreamingSendDraft;
                    finalMessage.content = finalMessage.content || '';
                  }

                  if (localStreamingBooking) {
                    finalMessage.booking = localStreamingBooking;
                    finalMessage.content = finalMessage.content || '';
                  }

                  // For a plain streamed-text reply, let the reveal buffer
                  // finish typing out its tail before swapping the preview for
                  // the finished message — otherwise the last un-revealed chars
                  // snap in all at once. Rich replies (images/posts/blog/etc.)
                  // set content to a short canned string, so nothing to wait on.
                  const isPlainStreamedText =
                    !localStreamingGeneratedImages &&
                    !localStreamingGeneratedPosts &&
                    !localStreamingBlog &&
                    !localStreamingOutreachEmail;
                  if (isPlainStreamedText) {
                    await waitForRevealDrain();
                  }

                  setMessages((prev) => [...prev, finalMessage]);
                  setContentImmediate('');
                  setStreamingThinkingContent('');
                  setStreamingActions(null);
                  setStreamingUiActions(null);
                  setStreamingCitations(null);
                  setStreamingGeneratedImages(null);
                  setStreamingGeneratedPosts(null);
                }
                abortRef.current = null;
                setIsLoading(false);
                setCurrentStatus(null);
                setCurrentToolName(null);
                // The reply is committed above, so the card lands after it in the thread.
                if (gbpReviewRequested) onGbpReviewRequested?.({ threadId: localThreadId, plan: gbpPlan });
                // If this was a deep-research stream and the document never arrived via SSE
                // (connection dropped before Perplexity finished), start polling for it.
                if (isDeepResearchRequest && !receivedResearchDoc) {
                  const currentThreadId = threadId;
                  if (currentThreadId) onDeepResearchDisconnected?.(currentThreadId, deepResearchStartedAt);
                }
                // Same disconnect handling for a bulk image batch whose terminal images
                // event never arrived (connection dropped mid-batch, navigate-away, etc.).
                if (isBulkImageBatchRequest && !receivedBulkImages) {
                  const currentThreadId = threadId;
                  if (currentThreadId) onBulkImageDisconnected?.(currentThreadId, bulkImageBatchStartedAt);
                }
                break;
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (!line.trim()) continue;
                // SSE comment lines (e.g. ": keepalive" heartbeats sent during long
                // image generation) are not data — ignore them per the SSE spec.
                if (line.startsWith(':')) continue;

                if (line.startsWith('event: ')) {
                  // Capture event type
                  currentEvent = line.substring(7).trim();
                  debug.log('Event type captured:', currentEvent);
                } else if (line.startsWith('data: ')) {
                  try {
                    const jsonStr = line.substring(6);
                    const data = JSON.parse(jsonStr);
                    debug.log('Processing data for event:', currentEvent, 'Data:', data);

                    // Handle UGC video events (can come through content or as a separate event)
                    if (data.type === 'ugc_video') {
                      debug.log('UGC Video event received:', data);

                      if (data.status === 'COMPLETED' && data.cloudfront_url) {
                        // Add a message with the video preview
                        const videoMessage: Message = {
                          role: 'assistant',
                          content: 'Your video is ready!',
                          ugcVideo: {
                            videoUrl: data.cloudfront_url,
                            videoId: data.video_id,
                            status: data.status,
                          },
                        };
                        setMessages((prev) => [...prev, videoMessage]);
                        onUGCVideoEvent?.(data);
                      } else if (data.status === 'FAILED') {
                        setError(data.error || 'Failed to generate video');
                      }
                      continue; // Skip stream handler for UGC video
                    }

                    // New message types: tool action (in progress) and action result (e.g. image_urls)
                    if (data.role === 'assistant' && data.type === 'action') {
                      const actionMessage: Message = {
                        role: 'assistant',
                        content: data.content || 'Working...',
                        type: 'action',
                        action_name: data.action_name,
                      };
                      setMessages((prev) => [...prev, actionMessage]);
                      continue;
                    }
                    if (data.role === 'assistant' && data.type === 'action_result') {
                      const resultMessage: Message = {
                        role: 'assistant',
                        content: data.content || '',
                        type: 'action_result',
                        image_urls: Array.isArray(data.image_urls) ? data.image_urls : [],
                      };
                      setMessages((prev) => [...prev, resultMessage]);
                      continue;
                    }

                    // Check for action metadata (social post created) before stream handler
                    if (data.type === 'social_post_created' && data.post_data && data.actions) {
                      const actionsData: MessageActions = {
                        type: 'social_post_created',
                        post_data: data.post_data,
                        actions: data.actions,
                      };
                      localStreamingActions = actionsData;
                      setStreamingActions(actionsData);
                    }

                    // Use stream handler utility
                    const result = handleStreamEvent(currentEvent, data, {
                      onStart: (startData) => {
                        try {
                          if (startData.model) {
                            localAnsweringModel = startData.model;
                          }
                          if (startData.thread_id) {
                            localThreadId = startData.thread_id;
                            setThreadId(startData.thread_id);
                          }
                          if (startData.conversation_id) {
                            setConversationId(startData.conversation_id);
                          }
                          if (startData.starter_actions && Array.isArray(startData.starter_actions)) {
                            if (isCommunicationAgentType(agentType)) {
                              setStarterActions?.([...COMMUNICATION_AGENT_STARTER_ACTIONS]);
                            } else {
                              const starters: UIAction[] = startData.starter_actions.map((a: any) => ({
                                type: a.type,
                                label: a.label,
                                payload: a.payload,
                              }));
                              setStarterActions?.(starters);
                            }
                          }
                          if (!conversationTitle && messages.length > 0) {
                            const firstUserMsg = messages.find((m) => m.role === 'user');
                            if (firstUserMsg) {
                              const title = generateConversationTitle(firstUserMsg.content);
                              setConversationTitle(title);
                            }
                          }
                          // Deep research has a ~60s silent crawl phase before streaming starts.
                          // Show the spinner immediately so the user isn't staring at nothing.
                          if (startData.model === 'sonar-deep-research') {
                            isDeepResearchRequest = true;
                            deepResearchStartedAt = Date.now();
                            setCurrentStatus('tool_executing');
                            setCurrentToolName('deep_research');
                          }
                        } catch (e) {
                          debug.error('Failed to extract thread metadata from start event:', e);
                        }
                      },
                      onSuggestedActions: (actions) => {
                        // The Business Profile card is this turn's answer: no follow-up chips
                        // between the reply and the card.
                        if (gbpReviewRequested) return;
                        localStreamingUiActions = actions;
                        setStreamingUiActions(actions);
                      },
                      onScript: (delta, fullText) => {
                        if (fullText !== undefined) {
                          localAccumulatedScript = fullText;
                        } else if (delta) {
                          localAccumulatedScript += delta;
                        }
                        onScript?.(delta, fullText);
                      },
                      onSocialDescription: (items) => {
                        onSocialDescription?.(items);
                      },
                      onThinking: (delta, text) => {
                        setCurrentStatus('thinking');
                        setCurrentToolName(null);
                        if (delta) {
                          localAccumulatedThinking += delta;
                          setStreamingThinkingContent(localAccumulatedThinking);
                        } else if (text) {
                          localAccumulatedThinking = text;
                          setStreamingThinkingContent(text);
                        }
                      },
                      onToolSelected: (toolName) => {
                        setCurrentStatus('tool_selected');
                        setCurrentToolName(toolName);
                        setToolSteps((prev) => [...prev, { tool: toolName, state: 'running' }]);
                        // A second generate_image_batch call later in the same turn should
                        // show the reassurance card again, not stay hidden from the first call's images.
                        if (toolName === 'generate_image_batch') {
                          setBulkImagesReady(false);
                          isBulkImageBatchRequest = true;
                          bulkImageBatchStartedAt = Date.now();
                        }
                        // Only a real blog turn selects output_blog (general prompts in blog
                        // mode are answered as text). Open the blog panel's loading view now.
                        if (toolName === 'output_blog') {
                          onBlogGenerationStart?.();
                        }
                      },
                      onToolExecuting: (toolName) => {
                        setCurrentStatus('tool_executing');
                        setCurrentToolName(toolName);
                      },
                      onGbpPlan: (plan) => {
                        gbpPlan = plan;
                      },
                      onGbpRowUpdate: (update) => onGbpRowUpdate?.(update),
                      onGbpPlanProgress: (stage) => {
                        setToolSteps((prev) =>
                          prev.map((s) =>
                            s.tool === 'review_google_business_profile' && s.state === 'running' ? { ...s, stage } : s
                          )
                        );
                      },
                      onToolCall: (toolData) => {
                        // The Business Profile card opens when the turn ends; until then
                        // its step shows the card's loading state (opensCard).
                        const opensGbpCard = isGbpReviewToolCall(toolData);
                        setToolSteps((prev) =>
                          prev.map((s) =>
                            s.tool === toolData.tool_name && s.state === 'running'
                              ? { ...s, state: 'done' as const, ...(opensGbpCard ? { opensCard: true } : {}) }
                              : s
                          )
                        );
                        if (opensGbpCard) {
                          gbpReviewRequested = true;
                        }
                        if (toolData.tool_name === 'ask_user') {
                          const question = parseAskUserResult(toolData.result);
                          if (question) onAskUser?.(question);
                        }
                        if (toolData.tool_name === 'generate_image' && toolData.result) {
                          try {
                            const result =
                              typeof toolData.result === 'string'
                                ? JSON.parse(toolData.result)
                                : toolData.result;
                            if (result.urls && Array.isArray(result.urls)) {
                              const imageData = {
                                prompt: result.prompt || '',
                                provider: result.provider || 'unknown',
                                urls: result.urls,
                              };
                              localStreamingGeneratedImages = imageData;
                              setStreamingGeneratedImages(imageData);
                            }
                          } catch (e) {
                            debug.error('Failed to parse image generation result:', e);
                          }
                        }
                        if (toolData.tool_name === 'ghl_get_availability' && toolData.result != null) {
                          try {
                            const raw =
                              typeof toolData.result === 'string'
                                ? toolData.result
                                : JSON.stringify(toolData.result);
                            const parsed = parseAvailabilityToolResult(raw);
                            if (parsed) {
                              localStreamingAvailability = parsed;
                            }
                          } catch (e) {
                            debug.error('Failed to parse availability tool result:', e);
                          }
                        }

                        if (toolData.tool_name === 'ghl_send_message' && toolData.result != null) {
                          try {
                            const raw =
                              typeof toolData.result === 'string'
                                ? toolData.result
                                : JSON.stringify(toolData.result);
                            const parsed = parseSendDraftToolResult(raw);
                            if (parsed) {
                              localStreamingSendDraft = parsed;
                            }
                          } catch (e) {
                            debug.error('Failed to parse send draft tool result:', e);
                          }
                        }

                        if (toolData.tool_name === 'ghl_book_appointment' && toolData.result != null) {
                          try {
                            const raw =
                              typeof toolData.result === 'string'
                                ? toolData.result
                                : JSON.stringify(toolData.result);
                            const parsed = parseBookingToolResult(raw);
                            if (parsed) {
                              localStreamingBooking = parsed;
                            }
                          } catch (e) {
                            debug.error('Failed to parse booking tool result:', e);
                          }
                        }

                        if (toolData.tool_name === 'ghl_search_contact' && toolData.result != null) {
                          try {
                            const raw = typeof toolData.result === 'string' ? toolData.result : JSON.stringify(toolData.result);
                            const parsed = parseContactSearchResult(raw);
                            if (parsed) {
                              localStreamingGhlContacts = parsed;
                              setStreamingGhlContacts(parsed);
                            }
                          } catch (e) {
                            debug.error('Failed to parse search contact tool result:', e);
                          }
                        }

                        if (toolData.tool_name === 'ghl_get_messages' && toolData.result != null) {
                          try {
                            const raw = typeof toolData.result === 'string' ? toolData.result : JSON.stringify(toolData.result);
                            const parsed = parseGetMessagesResult(raw);
                            if (parsed) {
                              localStreamingGhlConversation = parsed;
                              setStreamingGhlConversation(parsed);
                            }
                          } catch (e) {
                            debug.error('Failed to parse get messages tool result:', e);
                          }
                        }

                        if (toolData.tool_name === 'ghl_list_conversations' && toolData.result != null) {
                          try {
                            const raw = typeof toolData.result === 'string' ? toolData.result : JSON.stringify(toolData.result);
                            const parsed = parseListConversationsResult(raw);
                            if (parsed) {
                              localStreamingGhlInbox = parsed;
                              setStreamingGhlInbox(parsed);
                            }
                          } catch (e) {
                            debug.error('Failed to parse list conversations tool result:', e);
                          }
                        }

                        setCurrentStatus('generating');
                        setCurrentToolName(null);
                      },
                      onGenerating: () => {
                        setCurrentStatus('generating');
                        setCurrentToolName(null);
                      },
                      onSocialPosts: (posts, actions) => {
                        setToolSteps((prev) =>
                          prev.map((s) =>
                            s.tool === 'output_social_posts' && s.state === 'running'
                              ? { ...s, state: 'done' as const }
                              : s
                          )
                        );
                        debug.log('Received social_posts event, setting posts:', posts);
                        const postsData = { posts };
                        localStreamingGeneratedPosts = postsData;
                        setStreamingGeneratedPosts(postsData);
                        onSocialPostsPanelRequest?.(postsData);
                        if (actions && actions.length > 0) {
                          localStreamingUiActions = actions;
                          setStreamingUiActions(actions);
                        }
                        setStreamingContent((prev: string) => prev || 'Here are your social posts! 📝');
                      },
                      onImages: (prompt, provider, urls, actions, error, isBatch) => {
                        // Accumulate URLs across multiple generate_image tool calls so all images
                        // appear in a single gallery instead of replacing each other one by one.
                        const existingUrls = localStreamingGeneratedImages?.urls ?? [];
                        const mergedUrls = [...existingUrls, ...urls];
                        const imageData = {
                          prompt: prompt || localStreamingGeneratedImages?.prompt || '',
                          provider: provider || localStreamingGeneratedImages?.provider || '',
                          urls: mergedUrls,
                          ...(error && { error }),
                        };
                        localStreamingGeneratedImages = imageData;
                        setStreamingGeneratedImages(imageData);
                        if (isBatch) {
                          setBulkImagesReady(true);
                          receivedBulkImages = true;
                        }
                        if (actions && actions.length > 0) {
                          localStreamingUiActions = actions;
                          setStreamingUiActions(actions);
                        }
                        setStreamingContent((prev: string) => prev || (error ? '' : 'Here are your generated images! 🎨'));
                      },
                      onBlog: (blog) => {
                        setToolSteps((prev) =>
                          prev.map((s) =>
                            s.tool === 'output_blog' && s.state === 'running'
                              ? { ...s, state: 'done' as const }
                              : s
                          )
                        );
                        debug.log('Received blog event, opening blog editor:', blog);
                        localStreamingBlog = blog;
                        onBlogPanelRequest?.(blog);
                        // Mirror the blog into the chat thread (left) so the user can read
                        // it inline as well as edit it in the right-panel canvas.
                        accumulatedText = blogToChatMarkdown(blog);
                        setContentImmediate(accumulatedText);
                      },
                      onOutreachEmail: (email) => {
                        setToolSteps((prev) =>
                          prev.map((s) =>
                            s.tool === 'output_outreach_email' && s.state === 'running'
                              ? { ...s, state: 'done' as const }
                              : s
                          )
                        );
                        debug.log('Received outreach_email event:', email);
                        localStreamingOutreachEmail = email;
                        // No right-panel canvas for this chip — the card renders inline
                        // in the chat bubble (see MessageBubble/OutreachEmailCard).
                        accumulatedText = accumulatedText || 'Here’s your outreach email draft ✉️';
                        setContentImmediate(accumulatedText);
                      },
                      onCalls: (calls, truncated) => {
                        setToolSteps((prev) =>
                          prev.map((s) =>
                            s.tool === 'output_calls' && s.state === 'running'
                              ? { ...s, state: 'done' as const }
                              : s
                          )
                        );
                        debug.log('Received calls event:', calls.length, 'truncated:', truncated);
                        localStreamingCalls = { calls, truncated };
                      },
                      onCardContacts: (cardContacts) => {
                        setToolSteps((prev) =>
                          prev.map((s) =>
                            s.tool === 'output_card_contacts' && s.state === 'running'
                              ? { ...s, state: 'done' as const }
                              : s
                          )
                        );
                        debug.log('Received card_contacts event:', cardContacts.contacts.length, 'contact(s)');
                        localStreamingCardContacts = cardContacts;
                      },
                      onResearchDocument: (doc) => {
                        receivedResearchDoc = true;
                        setCurrentStatus(null);
                        setCurrentToolName(null);
                        // Drop the "Generating your research report…" holding text so it
                        // isn't committed as a stray bubble next to the document card.
                        accumulatedText = '';
                        setContentImmediate('');
                        const msg: Message = {
                          id: `research-${Date.now()}`,
                          role: 'assistant',
                          content: '',
                          type: 'research_document',
                          researchDocument: doc,
                        };
                        setMessages((prev) => [...prev, msg]);
                      },
                      onContentWithActions: (content, actions) => {
                        setCurrentStatus(null);
                        setCurrentToolName(null);
                        accumulatedText = content;
                        setContentImmediate(content);
                        if (gbpReviewRequested) return;
                        localStreamingUiActions = actions;
                        setStreamingUiActions(actions);
                      },
                      onContent: (delta, text) => {
                        setCurrentStatus(null);
                        setCurrentToolName(null);

                        if (delta) {
                          accumulatedText += delta;
                          revealContent(accumulatedText);
                        } else if (text) {
                          accumulatedText = text;
                          revealContent(accumulatedText);
                        }
                      },
                      onCitations: (citations) => {
                        localCitations = citations;
                        setStreamingCitations(citations);
                      },
                      onTitle: (title, threadIdFromEvent) => {
                        try {
                          if (title) {
                            setConversationTitle(title);
                          }
                          if (threadIdFromEvent) {
                            setThreadId(threadIdFromEvent);
                          }
                        } catch (e) {
                          debug.error('Failed to extract title from title event:', e);
                        }
                      },
                      onEnd: (endData) => {
                        try {
                          if (endData.thread_id) {
                            localThreadId = endData.thread_id;
                            setThreadId(endData.thread_id);
                            onThreadChange?.(endData.thread_id);
                          }
                          if (endData.conversation_id) {
                            setConversationId(endData.conversation_id);
                          }
                          if (Array.isArray(endData.citations) && endData.citations.length > 0) {
                            localCitations = endData.citations;
                          }
                        } catch (e) {
                          debug.error('Failed to extract thread metadata from end event:', e);
                        }
                        setCurrentStatus(null);
                        setCurrentToolName(null);
                      },
                      onError: (error) => {
                        debug.error('Stream error event received:', error);
                        setError(error);
                        setIsLoading(false);
                        setCurrentStatus(null);
                        setCurrentToolName(null);
                        setMessages((prev) => prev.slice(0, -1));
                      },
                      onToolError: (toolName, errorMsg) => {
                        debug.error('Tool error:', toolName, errorMsg);
                        setError(`Tool error (${toolName}): ${errorMsg}`);
                        setCurrentStatus(null);
                        setCurrentToolName(null);
                      },
                    });

                    if (result.shouldReturn) {
                      if (result.error) {
                        setError(result.error);
                        setIsLoading(false);
                        setCurrentStatus(null);
                        setCurrentToolName(null);
                        setMessages((prev) => prev.slice(0, -1));
                      }
                      return;
                    }

                    // Reset event type after processing
                    currentEvent = '';
                  } catch (e) {
                    const text = line.substring(6).trim();
                    if (text) {
                      accumulatedText += text;
                      revealContent(accumulatedText);
                    }
                  }
                } else {
                  // Try to parse as JSON directly (fallback)
                  try {
                    const data = JSON.parse(line);

                    // Check for action metadata
                    if (
                      data.type === 'social_post_created' &&
                      data.post_data &&
                      data.actions
                    ) {
                      setStreamingActions({
                        type: 'social_post_created',
                        post_data: data.post_data,
                        actions: data.actions,
                      });
                    }

                    if (data.delta) {
                      accumulatedText += data.delta;
                      revealContent(accumulatedText);
                    } else if (data.text || data.content) {
                      accumulatedText = data.text || data.content;
                      revealContent(accumulatedText);
                    }
                  } catch (e) {
                    if (line.trim()) {
                      accumulatedText += line;
                      revealContent(accumulatedText);
                    }
                  }
                }
              }
            }
          } catch (err) {
            const isAbort = err instanceof Error && err.name === 'AbortError';
            if (!isAbort) debug.error('Stream read error:', err);
            abortRef.current = null;
            setCurrentStatus(null);
            setCurrentToolName(null);
            setIsLoading(false);
            // Deep research: if the SSE dropped before the document arrived and the user
            // didn't manually abort, this is a RECOVERABLE disconnect — the report is
            // still being generated server-side and polling will deliver it. Start
            // polling and stop here: no error banner, no stray "Generating…" bubble
            // (accumulatedText only holds the holding message), no message trimming.
            if (isDeepResearchRequest && !receivedResearchDoc && !isAbort) {
              const currentThreadId = threadId;
              if (currentThreadId) onDeepResearchDisconnected?.(currentThreadId, deepResearchStartedAt);
              setContentImmediate('');
            } else if (isBulkImageBatchRequest && !receivedBulkImages && !isAbort) {
              // Same recoverable-disconnect handling as deep research: the batch keeps
              // generating server-side, polling picks up the result.
              const currentThreadId = threadId;
              if (currentThreadId) onBulkImageDisconnected?.(currentThreadId, bulkImageBatchStartedAt);
              setContentImmediate('');
            } else if (accumulatedText) {
              // Keep partial content whether it was an abort or a real error.
              // Deep research can also drop the connection after delivering all content.
              if (!isAbort) debug.log('Stream ended with error but content exists — keeping partial result');
              const finalMessage: Message = {
                id: `msg-${Date.now()}`,
                role: 'assistant',
                content: accumulatedText,
                answeringModel: localAnsweringModel,
                citations: localCitations.length > 0 ? localCitations : undefined,
              };
              // Append — there is no assistant placeholder in `messages` to replace here
              // (the live bubble lives in streamingContent, not in the array). Using
              // slice(0, -1) previously dropped the user's own message on stop/error.
              setMessages((prev) => [...prev, finalMessage]);
              setContentImmediate('');
            } else if (!isAbort) {
              setError(
                err instanceof Error
                  ? err.message
                  : 'Failed to read stream. Please try again.'
              );
              setMessages((prev) => prev.slice(0, -1));
            }
            // Aborted with no streamed content — user message stays, no error shown.
          }
        };

        void readStream().finally(release);
      } catch (err) {
        release();
        const isAbort = err instanceof Error && err.name === 'AbortError';
        abortRef.current = null;
        if (!isAbort) {
          debug.error('Error sending message:', err);
          if (err instanceof Error && (err as any).chatStreamError) {
            setError((err as any).chatStreamError);
          } else {
            setError(
              err instanceof Error
                ? err.message
                : 'Failed to send message. Please try again.'
            );
          }
          setMessages((prev) => prev.slice(0, -1));
        }
        setIsLoading(false);
      }
    },
    [
      agentId,
      activeCampaignId,
      agentType,
      customerId,
      idToken,
      model,
      selectedProvider,
      selectedChatModel,
      vectorStoreId,
      threadId,
      conversationId,
      messages,
      conversationTitle,
      setMessages,
      setIsLoading,
      setError,
      setStreamingContent,
      revealContent,
      setContentImmediate,
      waitForRevealDrain,
      setStreamingThinkingContent,
      setStreamingActions,
      setCurrentStatus,
      setCurrentToolName,
      setStreamingGeneratedImages,
      setStreamingGeneratedPosts,
      setStreamingUiActions,
      setStreamingCitations,
      setStarterActions,
      setThreadId,
      setConversationId,
      setConversationTitle,
      onThreadChange,
      onGbpReviewRequested,
      onGbpRowUpdate,
      onAskUser,
      onUGCVideoEvent,
      onScript,
      onScriptEnd,
      onSocialDescription,
      onSocialPostsPanelRequest,
      onBlogPanelRequest,
      onBlogGenerationStart,
      onClearUGCVideoSelections,
      setInputMessage,
      setSelectedImages,
    ]
  );

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { sendMessage, stopStreaming, toolSteps };
};
